import {
  Box, Button, FormControlLabel, InputAdornment, Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { ColorsConstants } from '../../../cssUtils/constants';
import { FieldLabel, Note, currencySymbol } from './ui';

const radioSx = { color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } };

const addDays = (base, days) => {
  const d = new Date(base || new Date().toISOString().split('T')[0]);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

// accountInfo: { currency, timezone_name, min_daily_budget (minor units) } from the account checks.
const BudgetSection = ({ budget: b, onChange, accountInfo, estimate }) => {
  const sym = currencySymbol(accountInfo?.currency || 'INR');
  const minMajor = accountInfo?.min_daily_budget ? Math.ceil(accountInfo.min_daily_budget / 100) : null;
  const conv = estimate?.daily_conversations;
  const daily = Number(b.daily) || 0;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
      <Stack spacing={2} sx={{ flex: 2, minWidth: 0, width: '100%' }}>
        <Box>
          <FieldLabel>Daily budget for this ad set</FieldLabel>
          <TextField
            fullWidth size="small" type="number" value={b.daily} inputProps={{ min: 0 }}
            onChange={(e) => onChange({ daily: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">{sym.trim()}</InputAdornment> }}
          />
          <Note sx={{ mt: 0.5 }}>
            {minMajor ? `Minimum for this ad account: ${sym}${minMajor}/day` : 'The minimum comes from your ad account once the checks have run.'}
          </Note>
        </Box>

        <Stack direction="row" flexWrap="wrap" gap={2}>
          <Box sx={{ flex: '1 1 200px' }}>
            <FieldLabel>Start date</FieldLabel>
            <TextField fullWidth size="small" type="date" value={b.startDate} onChange={(e) => onChange({ startDate: e.target.value })}
              inputProps={{ min: new Date().toISOString().split('T')[0] }} helperText={b.startDate ? '' : 'Empty = start as soon as Meta approves'} />
          </Box>
          <Box sx={{ flex: '1 1 260px' }}>
            <FieldLabel>End</FieldLabel>
            <RadioGroup value={b.endMode} onChange={(e) => onChange({ endMode: e.target.value })}>
              <FormControlLabel value="never" control={<Radio size="small" sx={radioSx} />} label={<Typography variant="body2">Run until I pause it</Typography>} />
              <FormControlLabel value="date" control={<Radio size="small" sx={radioSx} />} label={<Typography variant="body2">End on a date</Typography>} />
            </RadioGroup>
            {b.endMode === 'date' && (
              <Box sx={{ pl: 4 }}>
                <TextField size="small" type="date" value={b.endDate} onChange={(e) => onChange({ endDate: e.target.value })} />
                <Stack direction="row" gap={0.75} sx={{ mt: 1 }}>
                  {[7, 14, 30].map((d) => (
                    <Button key={d} size="small" variant="outlined" onClick={() => onChange({ endDate: addDays(b.startDate, d) })} sx={{ textTransform: 'none' }}>
                      {d} days
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
        <Note>Dates use the ad account's time zone{accountInfo?.timezone_name ? ` (${accountInfo.timezone_name})` : ''}.</Note>
      </Stack>

      <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: ColorsConstants.primaryBlue60, width: '100%' }}>
        <Typography variant="body2" fontWeight={700}>Estimated results</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ my: 0.5 }}>
          {conv ? `${conv.lower}–${conv.upper} conversations/day` : '—'}
        </Typography>
        {daily > 0 && <Note>{sym}{daily.toLocaleString('en-IN')}/day ≈ {sym}{(daily * 30).toLocaleString('en-IN')} per 30 days.</Note>}
        <Note sx={{ mt: 0.5 }}>Estimate from Meta; actual results vary.</Note>
      </Paper>
    </Stack>
  );
};

export default BudgetSection;
